import React from 'react';
import ServicesBlade from '../../pages/MechanicOrdersPage/ServicesBlade';
import ServiceEditForm from '../ServiceEditForm';
import ServiceSuggestionsPanel from './ServiceSuggestionsPanel';
import { Alert, Box, Button, CircularProgress, Dialog, DialogTitle, DialogContent, IconButton, Typography, Checkbox, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Collapse } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { StateManager } from '../../utilities/stateManager';
import firebase from '../../utilities/firebase';
import RequestManager from '../../utilities/requestManager';
import { useStackZIndex, ModalStackProvider } from '../../utilities/ModalStackContext';

export default function ServicesTab({ order, stockNumber, setOrder, hideEstimate = false }) {
  const myZ = useStackZIndex();
  const [serviceEditDialogOpen, setServiceEditDialogOpen] = React.useState(false);
  const [selectedServiceForEdit, setSelectedServiceForEdit] = React.useState(null);
  const [serviceSelectionExpanded, setServiceSelectionExpanded] = React.useState(false);
  const [estimating, setEstimating] = React.useState(false);
  const [retrying, setRetrying] = React.useState(false);

  const zeroHourServices = React.useMemo(() =>
    (order.services || []).filter(s => !s.time && s.status !== 'complete'),
    [order.services]
  );

  const hasServices = (order.services || []).length > 0;

  const estimateHours = async () => {
    if (estimating || zeroHourServices.length === 0) return;
    setEstimating(true);
    StateManager.setAlertAndOpen('Estimating hours in the background...', 'info');

    try {
      const response = await RequestManager.post({
        function: 'estimateServiceHours',
        variables: {
          services: zeroHourServices.map(s => ({ id: s.id, name: s.name })),
          car: (order.car && typeof order.car === 'object' && order.car.year)
            ? { year: order.car.year, make: order.car.make, model: order.car.model, trim: order.car.trim }
            : null,
        },
      });

      if (!response || !response.estimates) {
        StateManager.setAlertAndOpen('Failed to estimate hours', 'error');
        return;
      }

      // Don't overwrite a tag the user already set — only backfill untagged services.
      const tagById = Object.fromEntries((order.services || []).map(s => [s.id, s.tag]));

      // Write each estimate to Firestore
      await Promise.all(
        response.estimates.map(({ id, time, tag }) => {
          const updates = { time };
          if (tag && !tagById[id]) updates.tag = String(tag).trim();
          return firebase.firestore().collection('services').doc(id).update(updates);
        })
      );

      // Merge into local state so the UI reflects immediately
      const estimateMap = Object.fromEntries(response.estimates.map(({ id, time, tag }) => [id, { time, tag }]));
      setOrder(prev => ({
        ...prev,
        services: (prev.services || []).map(s => {
          const est = estimateMap[s.id];
          if (!est) return s;
          const next = { ...s, time: est.time };
          if (est.tag && !s.tag) next.tag = String(est.tag).trim();
          return next;
        }),
      }));

      StateManager.setAlertAndOpen(`Estimated hours for ${response.estimates.length} service(s)`, 'success');
    } catch {
      StateManager.setAlertAndOpen('Failed to estimate hours', 'error');
    } finally {
      setEstimating(false);
    }
  };
  
  // Get selected service IDs from order
  const selectedServiceIds = React.useMemo(() => {
    return order.selectedServiceIds || (order.services || []).map(s => s.id);
  }, [order.selectedServiceIds, order.services]);

  const handleServicesUpdate = (updatedServices) => {
    setOrder(prevOrder => ({
      ...prevOrder,
      services: updatedServices
    }));
  };

  const handleServiceEditClick = (service) => {
    setSelectedServiceForEdit(service);
    setServiceEditDialogOpen(true);
  };

  const handleServiceUpdate = async (updates) => {
    if (!selectedServiceForEdit) return;
    
    try {
      await firebase.firestore()
        .collection('services')
        .doc(selectedServiceForEdit.id)
        .update(updates);
      
      // Update local state
      const updatedServices = (order.services || []).map(service => 
        service.id === selectedServiceForEdit.id 
          ? { ...service, ...updates }
          : service
      );
      
      setOrder(prevOrder => ({
        ...prevOrder,
        services: updatedServices
      }));
      
      setSelectedServiceForEdit(prev => ({ ...prev, ...updates }));
    } catch (error) {
      console.error('Error updating service:', error);
      StateManager.setAlertAndOpen('Error updating service', 'error');
    }
  };

  const handleServiceDelete = async (serviceId) => {
    if (!serviceId) return;
    
    try {
      await firebase.firestore()
        .collection('services')
        .doc(serviceId)
        .delete();
      
      // Remove from local state
      const updatedServices = (order.services || []).filter(service => service.id !== serviceId);
      
      // Also remove from selectedServiceIds if it was selected
      const updatedSelectedIds = selectedServiceIds.filter(id => id !== serviceId);
      
      setOrder(prevOrder => ({
        ...prevOrder,
        services: updatedServices,
        selectedServiceIds: updatedSelectedIds
      }));
      
      // Update Firebase
      await firebase.firestore().doc(`orders/${stockNumber}`).update({ 
        selectedServiceIds: updatedSelectedIds 
      });
      StateManager.updateOrder({ selectedServiceIds: updatedSelectedIds });
      
      // Close the dialog
      setServiceEditDialogOpen(false);
      setSelectedServiceForEdit(null);
      
      StateManager.setAlertAndOpen('Service deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting service:', error);
      StateManager.setAlertAndOpen('Error deleting service', 'error');
    }
  };

  const handleServiceToggle = async (serviceId) => {
    const svc = (order.services || []).find(s => s.id === serviceId);
    if (svc && svc.declined) return; // declined services can't be selected for estimate/invoice
    try {
      const isSelected = selectedServiceIds.includes(serviceId);
      const newSelectedIds = isSelected
        ? selectedServiceIds.filter(id => id !== serviceId)
        : [...selectedServiceIds, serviceId];

      // Update local state
      setOrder(prevOrder => ({
        ...prevOrder,
        selectedServiceIds: newSelectedIds
      }));

      // Update Firebase
      await firebase.firestore().doc(`orders/${stockNumber}`).update({ 
        selectedServiceIds: newSelectedIds 
      });
      
      // Update order in state manager
      StateManager.updateOrder({ selectedServiceIds: newSelectedIds });
    } catch (error) {
      console.error('Error updating selected services:', error);
      StateManager.setAlertAndOpen('Error updating service selection', 'error');
    }
  };

  // The AI estimate runs as a second call after the order itself is written, so
  // it can fail on its own and leave the order with no services. The order keeps
  // the description it was created from, so the retry needs nothing re-typed.
  const retryGeneration = async () => {
    if (retrying) return;
    setRetrying(true);

    try {
      const response = await RequestManager.post({
        function: 'generateOrderServices',
        variables: { serviceOrderId: stockNumber, description: order.description },
      });

      if (!response || !response.success) {
        StateManager.setAlertAndOpen(
          (response && response.error) || 'Generating services failed again — add them by hand.',
          'error'
        );
        return;
      }

      // The modal's realtime listeners bring in the new rows and clear the flags.
      StateManager.setAlertAndOpen('Services generated', 'success');
    } catch (error) {
      console.error('Error retrying service generation:', error);
      StateManager.setAlertAndOpen('Generating services failed again — add them by hand.', 'error');
    } finally {
      setRetrying(false);
    }
  };

  return (
    <Box>
      {/* Only claim the AI is still working while the order is genuinely empty.
          The flag is cleared by the generate call, so an order that has lines and
          still carries it was written by a caller that never finished — the
          banner outlived the work and would otherwise never go away. */}
      {order.servicesGenerating && !hasServices && (
        <Alert
          icon={<CircularProgress size={18} />}
          severity="info"
          sx={{ mb: 2 }}
        >
          AI is generating services for this order… they'll appear here in a moment.
        </Alert>
      )}
      {order.servicesGenerationFailed && !hasServices && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={order.description ? (
            <Button color="inherit" size="small" onClick={retryGeneration} disabled={retrying}>
              {retrying ? <CircularProgress size={16} color="inherit" /> : 'Retry'}
            </Button>
          ) : undefined}
        >
          The AI estimate failed, so this order has no services.
          {order.description ? ' Retry it, or add them by hand.' : ' Add them by hand.'}
        </Alert>
      )}
      {!hideEstimate && zeroHourServices.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={estimating ? <CircularProgress size={16} /> : <AccessTimeIcon />}
            onClick={estimateHours}
            disabled={estimating}
          >
            {estimating ? 'Estimating...' : `Estimate Hours (${zeroHourServices.length})`}
          </Button>
        </Box>
      )}
      {StateManager.isManager() && <ServiceSuggestionsPanel orderId={stockNumber} />}
      <ServicesBlade
        order={{
          ...order,
          id: stockNumber
        }}
        onClose={() => {}}
        onUpdate={handleServicesUpdate}
        onServiceEdit={handleServiceEditClick}
      />

      {/* Service Selection Table - Collapsible at bottom */}
      <Paper sx={{ mt: 3 }}>
        <Box 
          onClick={() => setServiceSelectionExpanded(!serviceSelectionExpanded)}
          sx={{ 
            p: 2, 
            borderBottom: serviceSelectionExpanded ? '1px solid' : 'none',
            borderColor: 'divider',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            '&:hover': {
              bgcolor: 'action.hover',
            }
          }}
        >
          <Box>
            <Typography variant="h6">Select Services for Estimate/Invoice</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Check the services you want to include in estimates and invoices
            </Typography>
          </Box>
          <IconButton size="small">
            {serviceSelectionExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={serviceSelectionExpanded}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">Include</TableCell>
                  <TableCell>Service</TableCell>
                  <TableCell>Hours</TableCell>
                  <TableCell align="right">Cost</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(order.services || []).map((service) => (
                  <TableRow key={service.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={!service.declined && selectedServiceIds.includes(service.id)}
                        onChange={() => handleServiceToggle(service.id)}
                        disabled={order.disabled || service.declined}
                      />
                    </TableCell>
                    <TableCell sx={{ textDecoration: service.declined ? 'line-through' : 'none', color: service.declined ? 'text.disabled' : 'inherit' }}>
                      {service.name || '-'}
                      {service.declined && <Chip label="Declined" color="error" size="small" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />}
                    </TableCell>
                    <TableCell sx={{ color: service.declined ? 'text.disabled' : 'inherit' }}>{service.time || 0}</TableCell>
                    <TableCell align="right" sx={{ textDecoration: service.declined ? 'line-through' : 'none', color: service.declined ? 'text.disabled' : 'inherit' }}>${(service.cost || 0).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
                {(!order.services || order.services.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                      No services found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Collapse>
      </Paper>
      
      {/* Service Edit Dialog */}
      <ModalStackProvider value={myZ}>
      <Dialog
        open={serviceEditDialogOpen}
        onClose={() => setServiceEditDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        sx={{ zIndex: myZ }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Edit Service</Typography>
          <IconButton
            aria-label="close"
            onClick={() => setServiceEditDialogOpen(false)}
            sx={{ color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedServiceForEdit && (
            <ServiceEditForm
              service={selectedServiceForEdit}
              onUpdate={handleServiceUpdate}
              onDelete={handleServiceDelete}
            />
          )}
        </DialogContent>
      </Dialog>
      </ModalStackProvider>
    </Box>
  );
}

